import {
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
} from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'

import { Layout } from '#app/components/layout.tsx'
import type { RootLoaderType } from '#app/root'
import type { Handle } from '#app/types/index.js'
import { getLocaleFromRequest } from '#app/utils/i18n.ts'
import { mapImage } from '#app/utils/mappers.ts'
import { getUrl } from '#app/utils/misc.tsx'
import { getSocialMetas } from '#app/utils/seo.ts'
import { getAllVacancies, getPageBySlug } from '#app/utils/strapi.ts'

export const routes: Record<string, string> = {
  nl: 'vacatures',
  en: 'jobs',
}

export const handle: Handle = {
  getSitemapEntries: () => null,
}

export async function loader({ request }: LoaderFunctionArgs) {
  const locale = getLocaleFromRequest(request)

  const [page, vacancies] = await Promise.all([
    getPageBySlug(locale, locale === 'nl' ? 'vacatures' : 'jobs'),
    getAllVacancies(locale),
  ])

  return json({
    page,
    vacancies,
  })
}

export const meta: MetaFunction<typeof loader, { root: RootLoaderType }> = ({
  data,
  matches,
}) => {
  const rootData = matches.find(m => m.id === 'root')?.data

  if (data.page.SEO) {
    const meta = data.page.SEO
    return getSocialMetas({
      title: meta?.title,
      description: meta?.description,
      image: meta?.image.data?.attributes.url,
      url: getUrl(rootData.requestInfo),
    })
  } else {
    return [
      { title: 'Not Found' },
      { name: 'description', content: 'Not found' },
    ]
  }
}

export default function VacanciesRoute() {
  const data = useLoaderData<typeof loader>()
  const image = mapImage(data.page.image.data?.attributes)

  return (
    <Layout imageSrc={image.url} imageAlt={image.alt}>
      <div className="mt-auto w-full">
        <h1 className="mb-12 block text-h3 font-bold text-primary lg:text-h2">
          {data.page.title}
        </h1>
        <ul className="flex flex-col justify-stretch lg:max-w-96">
          {data.vacancies.map(vacancy => (
            <li key={vacancy.attributes.slug}>
              <Link
                to={vacancy.attributes.slug}
                className="block border-b border-muted py-6 text-2xl font-medium transition hover:border-muted-foreground focus:border-muted-foreground focus:outline-none"
              >
                {vacancy.attributes.title}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </Layout>
  )
}
